/**
 * Dialog on the Atlas `.modal` primitives: scrim click and Escape close it.
 * Portaled to <body> so an ancestor with backdrop-filter/transform (the glass
 * header) cannot become the containing block and clip the fixed scrim.
 * Focus stays inside while it is the top dialog: it moves in on opening (unless a
 * field took it with autoFocus), Tab and Shift+Tab wrap at the edges, and closing
 * returns it to the control that opened the dialog.
 */

import { useEffect, useId, useRef, type FormEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

import { cycleFocus } from './useFocusTrap';

/** Rank of each open dialog (later renders rank higher): Escape closes only the top one, so a
 *  confirmation over an editor does not take the editor with it. */
const openDialogs: number[] = [];
let rendered = 0;

/** Registers the dialog as open, and on closing hands focus back to what opened it. Returns the dialog's rank. */
function useOpenDialog(): RefObject<number> {
  const rank = useRef(0);
  if (rank.current === 0) rank.current = ++rendered;
  // Read while rendering: a child's autoFocus has moved focus by the time effects run.
  const opener = useRef<Element | null | undefined>(undefined);
  if (opener.current === undefined) opener.current = document.activeElement;
  const mounted = useRef(false);

  useEffect(() => {
    const id = rank.current;
    mounted.current = true;
    openDialogs.push(id);
    return () => {
      openDialogs.splice(openDialogs.indexOf(id), 1);
      mounted.current = false;
      // Focus goes back to what opened the dialog (if still on the page). Deferred a tick: StrictMode runs this
      // cleanup once on mount and remounts at once, and that must not pull focus out of an autofocused field.
      const el = opener.current;
      queueMicrotask(() => {
        // Not when something else took focus meanwhile (a status line the dialog's action moved it to).
        const taken = document.activeElement !== null && document.activeElement !== document.body;
        if (!mounted.current && !taken && el instanceof HTMLElement && el.isConnected) el.focus();
      });
    };
  }, []);
  return rank;
}

/** Focus inside the top dialog: moved in on opening (unless a field took it), Tab wraps, Escape closes. */
function useDialogFocus(dialog: RefObject<HTMLDivElement | null>, rank: RefObject<number>, onClose: () => void) {
  useEffect(() => {
    const root = dialog.current;
    if (root && !root.contains(document.activeElement)) root.focus();
  }, [dialog]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (Math.max(...openDialogs) !== rank.current) return; // only the top dialog
      if (event.key === 'Escape') onClose();
      else if (event.key === 'Tab' && !event.defaultPrevented && dialog.current) cycleFocus(event, dialog.current);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [dialog, rank, onClose]);
}

export function Modal({
  title,
  subtitle,
  size,
  onClose,
  onSubmit,
  footer,
  children,
}: {
  title: string;
  subtitle?: string;
  size?: 'narrow' | 'wide' | 'xwide';
  onClose: () => void;
  onSubmit?: () => void;
  footer: ReactNode;
  children: ReactNode;
}) {
  const titleId = useId();
  const dialog = useRef<HTMLDivElement>(null);
  const rank = useOpenDialog();
  useDialogFocus(dialog, rank, onClose);

  const body = (
    <>
      <div className="modal-head">
        <div>
          <h3 id={titleId}>{title}</h3>
          {subtitle && <div className="m-sub">{subtitle}</div>}
        </div>
        <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}>
          <X aria-hidden="true" />
        </button>
      </div>
      <div className="modal-body">{children}</div>
      <div className="modal-foot">{footer}</div>
    </>
  );

  return createPortal(
    <div className="modal-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={dialog}
        className={`modal${size ? ` ${size}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        {onSubmit ? (
          <form
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              onSubmit();
            }}
          >
            {body}
          </form>
        ) : (
          body
        )}
      </div>
    </div>,
    document.body,
  );
}
